import apiReviews from '@/api/reviews'
import apiItems from '@/api/items'
import errors from '@/api/errors'
import sorts from '@/mixins/sorts'

const debug = process.env.NODE_ENV !== 'production'

const state = {
  // internal
  internal: {
    pageTopImgs: {},
    linkTargetSku: undefined
  },
  query: {},
  // view
  count: 0,
  reviews: [],
  itemAdditionalInfo: {},
  itemImages: [],
  itemVariations: {},
  targetItem: {},
  // pager
  pager: {
    isPaged: false,
    currentIdx: 0,
    pages: [],
    history: [],
    origReferrer: ''
  }
}

const getters = {
  // internal
  defaultSortValue: state => state.internal.sortValue,
  defaultRefineValue: state => state.internal.refineValue,
  isRenderHelpful: state => state.internal.isRenderHelpful,
  isRenderClip: state => state.internal.isRenderClip,
  isRemovedOnUnclip: state => state.internal.isRemovedOnUnclip,
  isRenderModal: state => state.internal.isRenderModal,
  targetSku: state => state.internal.targetSku,
  pageTopImageMaxSize: state => state.internal.pageTopImgs.maxSize,
  linkTargetSku: state => state.internal.linkTargetSku,
  // view
  count: state => state.count,
  reviews: state => state.reviews,
  firstReview: state => state.reviews[0],
  itemAdditionalInfo: state => state.itemAdditionalInfo,
  itemImages: state => state.itemImages,
  itemVariations: state => state.itemVariations,
  targetItem: state => state.targetItem,
  targetItemCode: state => state.itemVariations.item_code,
  // pager
  pager: state => state.pager
}

const mutations = {
  initialize: (state, args) => {
    state.internal = Object.assign(state.internal, args)
  },
  updateQuery: (state, newQuery) => {
    state.query = Object.assign({}, state.query, newQuery)
  },
  setReviews: (state, data) => {
    state.count = data.count
    for (let list of data.lists) {
      let newImages = list.images.reduce((prev, img) => {
        if (img.path) {
          prev.push(img)
        }
        return prev
      }, [])
      list.images = newImages
    }
    state.reviews = data.lists
  },
  setReview: (state, data) => {
    if (data) {
      const newImages = data.images.filter(x => x.path)
      data.images = newImages
      // レビュー1件分のみ保持する
      state.reviews = [ data ]
    }
  },
  addReviews: (state, data) => {
    for (let list of data.lists) {
      let newImages = list.images.reduce((prev, img) => {
        if (img.path) {
          prev.push(img)
        }
        return prev
      }, [])
      list.images = newImages
    }
    const newLists = state.reviews.concat(data.lists)
    state.reviews = newLists
  },
  setItemAdditionalInfo: (state, data) => {
    const work = {}
    work.average = data.average
    work.count = data.count
    state.itemAdditionalInfo = work
  },
  setReviewImages: (state, data) => {
    const images = data.lists.map(rev => {
      return rev.images[0]
    })
    state.itemImages = images
  },
  setItemImagesByReviews: (state) => {
    const images = state.reviews.reduce((acc, rev) => {
      return rev.images.reduce((acc, img) => {
        if (img.path != null) {
          if (img.path) {
            acc.push(img)
          }
        }
        return acc
      }, acc)
    }, [])
    state.itemImages = images
  },
  addReviewImages: (state, data) => {
    const images = data.lists.reduce((acc, rev) => {
      return rev.images.reduce((_, img) => {
        if (img.path) {
          acc.push(img)
        }
        return acc
      }, [])
    }, [])
    const newLists = Object.assign(state.itemImages, images)
    state.itemImages = newLists
  },
  registReview: (state, args) => {
  },
  deleteReview: (state, args) => {
    const newLists = state.reviews.filter((val, index, array) => {
      return val.review_id !== args.reviewId
    })
    state.reviews = newLists
    state.count = state.count > 0 ? state.count - 1 : 0
  },
  setEntryHelpful: (state, args) => {
    for (let r of state.reviews) {
      if (r.review_id === args.reviewId) {
        r.is_entry_helpful = args.isEntryHelpful
        if (args.isEntryHelpful === 1) {
          r.is_entry_helpful_id = args.helpfulId
          ++r.count_helpful
        } else {
          delete r.is_entry_helpful_id
          --r.count_helpful
        }
        return
      }
    }
  },
  setEntryClip: (state, args) => {
    for (let r of state.reviews) {
      if (r.review_id === args.reviewId) {
        r.is_entry_clip = args.isEntryClip
        if (args.isEntryClip === 1) {
          r.is_entry_clip_id = args.clipId
        } else {
          delete r.is_entry_clip_id
        }
        return
      }
    }
  },
  setItemVariations: (state, data) => {
    state.itemVariations = data
    data.sku.forEach(item => {
      if (item.sku_code === state.internal.targetSku) {
        state.targetItem = item
      }
    })
  },
  initPager: (state, pagerData) => {
    state.pager = Object.assign({}, state.pager, pagerData, {
      isPaged: false
    })
  },
  goPrevPage: (state) => {
    state.pager = Object.assign({}, state.pager, {
      isPaged: true,
      currentIdx: state.pager.currentIdx - 1
    })
  },
  goNextPage: (state) => {
    state.pager = Object.assign({}, state.pager, {
      isPaged: true,
      currentIdx: state.pager.currentIdx + 1
    })
  },
  removeCurrentPage: (state) => {
    state.pager.pages.splice(state.pager.currentIdx, 1)
    state.pager.isPaged = true
  },
  setCurrentIdx: (state, reviewId) => {
    state.pager.currentIdx = state.pager.pages.indexOf(reviewId)
  },
  pushHistory: (state, reviewId) => {
    state.pager.history.push(reviewId)
  },
  popHistory: (state) => {
    return state.pager.history.pop()
  },
  removeFromHistory: (state, reviewId) => {
    state.pager.history = state.pager.history.filter(id => {
      return id !== reviewId
    })
  },
  setLinkTargetSku: (state, skuCode) => {
    state.internal.linkTargetSku = skuCode
  }
}

const actions = {
  /**
   * 初期化
   */
  initialize: ({ commit }, args) => {
    commit('initialize', args)
  },
  /**
   * レビュー取得 (一覧)
   */
  getReviews: ({ dispatch, commit }, args) => {
    dispatch('commons/setLoadingAll', { isLoading: true }, { root: true })
    dispatch('commons/clearGlobalError', {}, { root: true })
    const params = Object.assign({}, state.query, {
      status: args.status ? args.status : undefined,
      orderValue: sorts.methods.getOrderValue(state.internal.sortValue),
      sortValue: sorts.methods.getSortValue(state.internal.sortValue),
      start: 0,
      count: state.internal.pageSize,
      nicknames: args.targetUser ? [ args.targetUser ] : undefined,
      isSelfSearch: args.isSelfSearch ? args.isSelfSearch : undefined
    })
    return apiReviews.getReviews(params).then(data => {
      commit('updateQuery', params)
      commit('setReviews', data)
      dispatch('commons/setTargetNone', { isTargetNone: data.count === 0 }, { root: true })
    }).catch(error => {
      dispatch('commons/setGlobalError', error, { root: true })
    }).then(() => {
      dispatch('commons/setLoadingAll', { isLoading: false }, { root: true })
    })
  },
  /**
   * skuリストを取得
   */
  getItems: ({ dispatch, commit, rootState }) => {
    dispatch('commons/setLoadingAll', { isLoading: true }, { root: true })
    dispatch('commons/clearGlobalError', {}, { root: true })
    const params = {
      skuCode: state.internal.targetSku
    }
    return apiItems.getItemVariation(params).then(data => {
      commit('setItemVariations', data)
    }).catch(error => {
      dispatch('commons/setGlobalError', error, { root: true })
    }).then(() => {
      dispatch('commons/setLoadingAll', { isLoading: false }, { root: true })
    })
  },
  /**
   * レビュー取得 (商品別一覧)
   */
  getReviewsBySku: ({ dispatch, commit, rootState }) => {
    dispatch('commons/setLoadingAll', { isLoading: true }, { root: true })
    dispatch('commons/clearGlobalError', {}, { root: true })
    const skuList = state.itemVariations.sku.map((obj) => obj.sku_code)
    const params = Object.assign({}, state.query, {
      orderValue: sorts.methods.getOrderValue(state.internal.sortValue),
      sortValue: sorts.methods.getSortValue(state.internal.sortValue),
      start: 0,
      count: state.internal.pageSize,
      skuCodes: skuList
    })
    return apiReviews.getReviews(params).then(data => {
      commit('updateQuery', params)
      commit('setReviews', data)
      commit('setItemAdditionalInfo', data)
      dispatch('commons/setTargetNone', { isTargetNone: data.count === 0 }, { root: true })
    }).catch(error => {
      dispatch('commons/setGlobalError', error, { root: true })
    }).then(() => {
      dispatch('commons/setLoadingAll', { isLoading: false }, { root: true })
    })
  },
  /**
   * レビュー取得 (ID指定)
   */
  getReviewById: ({ dispatch, commit }, args) => {
    dispatch('commons/setLoadingAll', { isLoading: true }, { root: true })
    dispatch('commons/clearGlobalError', {}, { root: true })
    const params = Object.assign({}, state.query, {
      reviewId: args.reviewId,
      comments: args.comments
    })
    return apiReviews.getReviewById(params).then(data => {
      commit('updateQuery', params)
      commit('setReview', data)
      commit('storeComments/setComments', {
        count: data.count_comment,
        lists: data.comments
      }, { root: true })
    }).catch(error => {
      dispatch('commons/setGlobalError', error, { root: true })
    }).then(() => {
      dispatch('commons/setLoadingAll', { isLoading: false }, { root: true })
    })
  },
  /**
   * レビュー取得 (クリップのみ)
   */
  getClippedReviews: ({ dispatch, commit }) => {
    dispatch('commons/setLoadingAll', { isLoading: true }, { root: true })
    dispatch('commons/clearGlobalError', {}, { root: true })
    const params = Object.assign({}, state.query, {
      isClip: '1', // 1:クリップのみ
      orderValue: sorts.methods.getOrderValue(state.internal.sortValue),
      sortValue: sorts.methods.getSortValue(state.internal.sortValue),
      start: 0,
      count: state.internal.pageSize
    })
    return apiReviews.getReviews(params).then(data => {
      commit('updateQuery', params)
      commit('setReviews', data)
      dispatch('commons/setTargetNone', { isTargetNone: data.count === 0 }, { root: true })
    }).catch(error => {
      dispatch('commons/setGlobalError', error, { root: true })
    }).then(() => {
      dispatch('commons/setLoadingAll', { isLoading: false }, { root: true })
    })
  },
  /**
   * ソート
   */
  sortReviews: ({ dispatch, commit }, args) => {
    dispatch('commons/setLoadingAll', { isLoading: true }, { root: true })
    dispatch('commons/clearGlobalError', {}, { root: true })
    const params = Object.assign({}, state.query, {
      orderValue: args.orderValue,
      sortValue: args.sortValue,
      start: args.start,
      count: state.internal.pageSize
    })
    return apiReviews.getReviews(params).then(data => {
      commit('updateQuery', params)
      commit('setReviews', data)
      dispatch('commons/setTargetNone', { isTargetNone: data.count === 0 }, { root: true })
    }).catch(error => {
      dispatch('commons/setGlobalError', error, { root: true })
    }).then(() => {
      dispatch('commons/setLoadingAll', { isLoading: false }, { root: true })
    })
  },
  /**
   * 絞り込み
   */
  refineReviews: ({ dispatch, commit }, args) => {
    dispatch('commons/setLoadingAll', { isLoading: true }, { root: true })
    dispatch('commons/clearGlobalError', {}, { root: true })
    const params = Object.assign({}, state.query, {
      star: args.refineValue,
      start: args.start,
      count: state.internal.pageSize
    })
    return apiReviews.getReviews(params).then(data => {
      commit('updateQuery', params)
      commit('setReviews', data)
      dispatch('commons/setTargetNone', { isTargetNone: data.count === 0 }, { root: true })
    }).catch(error => {
      dispatch('commons/setGlobalError', error, { root: true })
    }).then(() => {
      dispatch('commons/setLoadingAll', { isLoading: false }, { root: true })
    })
  },
  /**
   * もっと見る
   */
  moreReviews: ({ dispatch, commit }) => {
    dispatch('commons/setLoadingMore', { isLoading: true }, { root: true })
    dispatch('commons/clearGlobalError', {}, { root: true })
    const params = Object.assign({}, state.query, {
      start: state.reviews.length,
      count: state.internal.pageSizeMore
    })
    return apiReviews.getReviews(params).then(data => {
      commit('updateQuery', params)
      commit('addReviews', data)
      dispatch('commons/setLoadingMore', { isLoading: false }, { root: true })
    }).catch(error => {
      dispatch('commons/setLoadingMore', { isLoading: false }, { root: true })
      if (error instanceof errors.AuthenticationError) {
        dispatch('commons/setGlobalError', error, { root: true })
      } else {
        return Promise.reject(Error('問題が発生したため処理を継続することができませんでした。'))
      }
    })
  },
  /**
   * 平均星数、全体件数
   */
  getReviewsForAdditionalInfo: ({ dispatch, commit }) => {
    dispatch('commons/clearGlobalError', {}, { root: true })
    const skuList = state.itemVariations.sku.map((obj) => obj.sku_code)
    const params = Object.assign({}, state.query, {
      start: 0,
      // レビュー自体は必要いので1件で十分
      count: 1,
      skuCodes: skuList
    })
    return apiReviews.getReviews(params).then(data => {
      commit('setItemAdditionalInfo', data)
    }).catch(error => {
      dispatch('commons/setGlobalError', error, { root: true })
    })
  },
  getReviewImages: ({ dispatch, commit, rootState }) => {
    dispatch('commons/clearGlobalError', {}, { root: true })
    const skuList = state.itemVariations.sku.map((obj) => obj.sku_code)
    const params = Object.assign({}, state.query, {
      isImage: '1',
      start: 0,
      count: state.internal.pageTopImgs.maxSize,
      skuCodes: skuList,
      sortValue: sorts.methods.getSortValue(state.internal.pageTopImgs.sortValue),
      orderValue: sorts.methods.getOrderValue(state.internal.pageTopImgs.sortValue)
    })
    return apiReviews.getReviews(params).then(data => {
      commit('setReviewImages', data)
    }).catch(error => {
      dispatch('commons/setGlobalError', error, { root: true })
    })
  },
  getReviewForItemImages: ({ dispatch, commit, rootState }) => {
    dispatch('commons/setLoadingAll', { isLoading: true }, { root: true })
    dispatch('commons/clearGlobalError', {}, { root: true })
    const skuList = state.itemVariations.sku.map((obj) => obj.sku_code)
    const params = Object.assign({}, state.query, {
      isImage: '1',
      start: 0,
      count: state.internal.pageSize,
      skuCodes: skuList
    })
    return apiReviews.getReviews(params).then(data => {
      commit('updateQuery', params)
      commit('setReviews', data)
      commit('setItemImagesByReviews')
      dispatch('commons/setTargetNone', { isTargetNone: Object.keys(state.itemImages).length === 0 }, { root: true })
    }).catch(error => {
      dispatch('commons/setGlobalError', error, { root: true })
    }).then(() => {
      dispatch('commons/setLoadingAll', { isLoading: false }, { root: true })
    })
  },
  /**
   * レビュー画像のもっと見る
   */
  moreImages: ({ dispatch, commit }) => {
    dispatch('commons/setLoadingMore', { isLoading: true }, { root: true })
    dispatch('commons/clearGlobalError', {}, { root: true })
    const params = Object.assign({}, state.query, {
      isImage: '1',
      start: state.reviews.length,
      count: state.internal.pageSizeMore
    })
    return apiReviews.getReviews(params).then(data => {
      commit('updateQuery', params)
      commit('addReviews', data)
      commit('setItemImagesByReviews')
      dispatch('commons/setLoadingMore', { isLoading: false }, { root: true })
    }).catch(error => {
      dispatch('commons/setLoadingMore', { isLoading: false }, { root: true })
      if (error instanceof errors.AuthenticationError) {
        dispatch('commons/setGlobalError', error, { root: true })
      } else {
        return Promise.reject(Error('問題が発生したため処理を継続することができませんでした。'))
      }
    })
  },
  /**
   * レビュー登録
   */
  registReview: ({ dispatch, commit }, args) => {
    dispatch('commons/setLoadingPost', { isLoading: true }, { root: true })
    dispatch('commons/clearGlobalError', {}, { root: true })
    const params = {
      skuCode: args.skuCode,
      itemCode: args.itemCode,
      star: args.star,
      caption: args.caption,
      description: args.description,
      image_1: args.image_1,
      image_2: args.image_2,
      image_3: args.image_3,
      image_4: args.image_4,
      image_5: args.image_5,
      tags: args.tags
    }
    return apiReviews.registReview(params).then(data => {
      commit('registReview', data)
      dispatch('commons/setLoadingPost', { isLoading: false }, { root: true })
    }).catch(error => {
      dispatch('commons/setLoadingPost', { isLoading: false }, { root: true })
      if (error instanceof errors.AuthenticationError) {
        dispatch('commons/setGlobalError', error, { root: true })
      } else if (error instanceof errors.InputValidationError) {
        return Promise.reject(Error(error.message))
      } else {
        return Promise.reject(Error('問題が発生したため処理を継続することができませんでした。'))
      }
    })
  },
  /**
   * レビュー登録 ペイロード情報取得
   */
  getRegistPayload: ({ dispatch }, args) => {
    const json = JSON.stringify(apiReviews.getRegistParams(args))
    const m = encodeURIComponent(json).match(/%[89ABab]/g)
    const size = json.length + (m ? m.length : 0)
    if (debug) {
      console.log('Payload: ' + json)
      console.log('Payload Size: ' + size)
    }
    return {
      json: json,
      size: size
    }
  },
  /**
   * レビュー削除
   */
  deleteReview: ({ dispatch, commit }, args) => {
    dispatch('commons/setLoadingPost', { isLoading: true }, { root: true })
    dispatch('commons/clearGlobalError', {}, { root: true })
    const params = {
      reviewId: args.reviewId
    }
    return apiReviews.deleteReview(params).then(data => {
      /* if (!state.query.reviewId) {
        commit('deleteReview', args)
      } */
      dispatch('commons/setLoadingPost', { isLoading: false }, { root: true })
      return data
    }).catch(error => {
      dispatch('commons/setLoadingPost', { isLoading: false }, { root: true })
      if (error instanceof errors.AuthenticationError) {
        dispatch('commons/setGlobalError', error, { root: true })
      } else {
        return Promise.reject(Error('問題が発生したため処理を継続することができませんでした。'))
      }
    })
  },
  /**
   * 役に立った登録/解除
   */
  setEntryHelpful: ({ commit }, args) => {
    commit('setEntryHelpful', args)
  },
  /**
   * クリップ登録/解除
   */
  setEntryClip: ({ commit }, args) => {
    commit('setEntryClip', args)
  },
  /**
   * クリップ解除したレビューを除去する
   * (内部的に除去するだけでAPIには問い合わせない)
   */
  removeReviewInternal: ({ commit }, args) => {
    commit('deleteReview', args)
  },
  /**
   * ページャー初期化
   */
  initPager: ({ dispatch, commit }, args) => {
    if (args.pagerData) {
      args.pagerData.history.push(state.reviews[0].review_id)
      commit('initPager', args.pagerData)
      return
    }
    dispatch('commons/clearGlobalError', {}, { root: true })
    const params = Object.assign({}, args.queryParams, {
      start: 0,
      count: 0
    })
    return apiReviews.getReviews(params).then(data => {
      const pagerData = data.lists.reduce((tmp, r, i) => {
        if (!r.deleted) {
          if (r.review_id === state.reviews[0].review_id) {
            tmp.currentIdx = i
          }
          tmp.pages.push(r.review_id)
        }
        return tmp
      }, {
        history: [state.reviews[0].review_id],
        pages: [],
        origReferrer: document.referrer
      })
      commit('initPager', pagerData)
    }).catch(error => {
      dispatch('commons/setGlobalError', error, { root: true })
    })
  },
  /**
   * ページャー前へ
   */
  goPrevPage: ({ commit }) => {
    commit('goPrevPage')
    return state.pager
  },
  /**
   * ページャー次へ
   */
  goNextPage: ({ commit }) => {
    commit('goNextPage')
    return state.pager
  },
  /**
   * ページャー 現在のページをページリストより削除
   */
  removeCurrentPage: ({ commit }) => {
    const reviewId = state.reviews[0].review_id
    commit('removeCurrentPage')
    commit('removeFromHistory', reviewId)
    const historyLen = state.pager.history.length
    commit('setCurrentIdx', state.pager.history[historyLen - 1])
    return state.pager
  },
  /**
   * ページャー 現在のページのインデックスを設定
   */
  setCurrentIdx: ({ commit }, reviewId) => {
    commit('setCurrentIdx', {
      reviewId: reviewId || state.reviews[0].review_id
    })
    return state.pager
  },
  setLinkTargetSku: ({ commit }, skuCode) => {
    commit('setLinkTargetSku', skuCode)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}



// WEBPACK FOOTER //
// ./src/store/modules/reviews.js